/**
 * Pure, render-free derivations for the Passkey / Device Detail surface.
 *
 * Every function here is total and side-effect free so routing,
 * classification, redaction and validation can be unit tested without a
 * DOM, a router, a ceremony or a network.
 *
 * Security contract enforced by this module:
 *  - no credential bytes, token hashes, secrets, wrapped keys or PRF
 *    output are ever read, derived or returned;
 *  - canonical machine values are passed through unchanged and are
 *    localized by the caller, never mutated here.
 */
import type {PersonPasskey} from "../../lib/types";

const UUID_RE=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The credential path parameter is a UUID. */
export function isValidCredentialId(value:string|null|undefined):value is string{
  return typeof value==="string"&&UUID_RE.test(value.trim());
}

/* -------------------------------------------------------------- capabilities */

/** Synced passkeys survive device loss; device-bound ones do not. */
export type BoundKind="synced"|"device-bound";

export function boundKind(passkey:Pick<PersonPasskey,"device_type"|"backed_up">):BoundKind{
  const raw=(passkey.device_type??"").replace(/^CredentialDeviceType\./,"").toUpperCase();
  return passkey.backed_up||raw==="MULTI_DEVICE"?"synced":"device-bound";
}

export type StateToneKind="positive"|"neutral"|"attention"|"critical";

export function statusTone(status:string):StateToneKind{
  const raw=status.toUpperCase();
  if(raw==="ACTIVE")return "positive";
  if(raw==="REVOKED"||raw==="BLOCKED"||raw==="FAILED")return "critical";
  if(raw==="EXPIRED"||raw==="PENDING"||raw==="SUSPENDED")return "attention";
  return "neutral";
}

export type TrustToneKind="positive"|"attention"|"critical"|"neutral";

export function trustStateTone(trustState:string|null|undefined):TrustToneKind{
  const raw=(trustState??"").toUpperCase();
  if(raw==="TRUSTED")return "positive";
  if(raw==="REVOKED")return "critical";
  if(raw==="RESTRICTED")return "attention";
  return "neutral";
}

export function postureTone(ok:boolean):StateToneKind{
  return ok?"positive":"attention";
}

/* --------------------------------------------------------------- safe values */

/**
 * Transports arrive as a JSON array of protocol names. Anything that is
 * not a short protocol token is dropped rather than rendered, so an
 * unexpected payload can never become a rendering or injection vector.
 */
const TRANSPORT_RE=/^[a-z][a-z0-9-]{0,31}$/;

export function safeTransports(
  transports:string[]|null|undefined,
):string[]{
  if(!Array.isArray(transports))return [];
  const out:string[]=[];
  for(const entry of transports){
    if(typeof entry!=="string")continue;
    const token=entry.trim().toLowerCase();
    if(TRANSPORT_RE.test(token)&&!out.includes(token))out.push(token);
  }
  return out.sort();
}

export type RelyingPartyKind="current"|"legacy"|"unknown";

export function relyingPartyKind(
  rpProfileId:string|null|undefined,
  rpKind:string|null|undefined,
):RelyingPartyKind{
  const raw=(rpKind??"").toUpperCase();
  if(raw==="LEGACY"||raw==="DEPRECATED"||raw==="RETIRED")return "legacy";
  if(raw==="CURRENT"||raw==="ACTIVE"||raw==="PRIMARY")return "current";
  // No explicit kind: a profile that is bound at all is the current one.
  return rpProfileId?"current":"unknown";
}

/**
 * The credential UUID is a non-secret identifier, but it is only
 * rendered when it is a well-formed UUID so an unexpected value from the
 * read model is never echoed into the page.
 */
export function safeCredentialReference(
  credentialUuid:string|null|undefined,
):string|null{
  return isValidCredentialId(credentialUuid)?credentialUuid:null;
}

export function displayName(passkey:Pick<PersonPasskey,"label">):string{
  return passkey.label.trim();
}

/* ------------------------------------------------------------ device posture */

export interface DevicePostureView{
  present:boolean;
  deviceLabel:string|null;
  platform:string|null;
  trustState:string|null;
  recoveryClass:string|null;
  lastSeenAt:string|null;
  lastVerifiedAt:string|null;
}

export function devicePosture(
  passkey:PersonPasskey|null|undefined,
):DevicePostureView{
  const none:DevicePostureView={
    present:false,
    deviceLabel:null,
    platform:null,
    trustState:null,
    recoveryClass:null,
    lastSeenAt:null,
    lastVerifiedAt:null,
  };
  if(!passkey||!passkey.device_posture_id)return none;
  // Posture attributes are only surfaced when a posture row is actually
  // linked, so a stale denormalized value can never be presented as fact.
  return{
    present:true,
    deviceLabel:passkey.device_label??null,
    platform:passkey.platform??null,
    trustState:passkey.trust_state??null,
    recoveryClass:passkey.recovery_class??null,
    lastSeenAt:passkey.posture_last_seen_at??null,
    lastVerifiedAt:passkey.posture_last_verified_at??null,
  };
}

/* ------------------------------------------------------------------- actions */

/**
 * Destructive action availability, derived from what the backend
 * actually supports rather than from UI intent.
 *
 * ``rename`` is an administrator action protected by a fresh passkey /
 * PRF step-up. ``revoke`` is intentionally NOT available here: the
 * backend revoke path is scoped to the credential owner's own subject,
 * so an administrator cannot revoke another person's passkey.
 */
export type PasskeyAction="rename"|"revoke";

export interface PasskeyActionAvailability{
  rename:boolean;
  revoke:boolean;
  lostDevice:boolean;
}

/** Minimum audit reason length enforced by the backend. */
export const RENAME_REASON_MIN_LENGTH=5;
/** Maximum label length enforced by the backend. */
export const RENAME_LABEL_MAX_LENGTH=255;

export function actionAvailability(
  passkey:PersonPasskey|null|undefined,
):PasskeyActionAvailability{
  const active=Boolean(passkey)&&passkey?.status?.toUpperCase()==="ACTIVE";
  return{
    rename:active,
    // No administrator revoke path exists in the backend.
    revoke:false,
    // No lost-device recovery flow exists in the backend.
    lostDevice:false,
  };
}

export interface RenameValidation{
  ok:boolean;
  label:string;
  reason:string;
}

export function validateRename(input:{
  label:string;
  reason:string;
}):RenameValidation{
  const label=input.label.trim();
  const reason=input.reason.trim();
  if(!label||label.length>RENAME_LABEL_MAX_LENGTH||reason.length<RENAME_REASON_MIN_LENGTH){
    return{ok:false,label,reason};
  }
  return{ok:true,label,reason};
}

/* ------------------------------------------------------------------ loading */

export type PasskeyLoadFailure="not-found"|"guarded"|"generic";

export function classifyPasskeyError(error:string|null):PasskeyLoadFailure|null{
  if(!error)return null;
  if(/passkey_session_required|authentication_required/i.test(error))return "guarded";
  if(/permission_denied|request_failed_40[139]|request_failed_5\d\d/i.test(error)){
    return "generic";
  }
  return "generic";
}

/**
 * Locate one passkey in the organization list.
 * Returns null when the reference is not present in the tenant scope,
 * which the surface renders as a not-found state rather than an error.
 */
export function findPasskey(
  passkeys:PersonPasskey[]|null|undefined,
  credentialUuid:string,
):PersonPasskey|null{
  if(!Array.isArray(passkeys))return null;
  return passkeys.find(p=>p?.credential_uuid===credentialUuid)??null;
}

/**
 * Group passkeys by owner for the People list integration.
 * Sorted by display label so the list is stable across refreshes.
 */
export function sortPasskeys(passkeys:PersonPasskey[]):PersonPasskey[]{
  return [...passkeys].sort((a,b)=>
    (a.label??"").localeCompare(b.label??"")||a.credential_uuid.localeCompare(b.credential_uuid));
}